import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LicenceBudgetAlert } from "@/components/LicenceBudget";
import { BillsDueTile } from "@/components/BillsDueTile";
import { formatDistanceToNow, parseISO } from "date-fns";
import { RenewalQuotationDialog } from "@/components/RenewalQuotationDialog";import {
  Users, Package, FileText, Truck, AlertTriangle, Wallet, ArrowRight,
  ShieldCheck, LifeBuoy, Boxes, Activity, Plus, Pencil, Trash2, RefreshCw,
  ArrowRightCircle, Check, AlarmClock, MessageCircle, Database, ShieldAlert,
  KeyRound, FileSignature, ClipboardList, Flame,
} from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { openWaForInvoice } from "@/lib/whatsapp";
import { fmtMoney, fmtDate } from "@/lib/format";
import { PageHeader, StatusBadge } from "@/components/Common";
import { Button } from "@/components/ui/button";

// ─────────────────────────────────────────────────────────────────────
// Dashboard-wide activity feed (uses /api/audit-logs)
// ─────────────────────────────────────────────────────────────────────
const ACT_ICON = {
  created: Plus, updated: Pencil, deleted: Trash2,
  status_changed: RefreshCw, converted: ArrowRightCircle, approved: Check,
};
const ACT_TONE = {
  created: "bg-emerald-100 text-emerald-700 ring-emerald-200",
  updated: "bg-sky-100 text-sky-700 ring-sky-200",
  deleted: "bg-red-100 text-red-700 ring-red-200",
  status_changed: "bg-amber-100 text-amber-800 ring-amber-200",
  converted: "bg-indigo-100 text-indigo-700 ring-indigo-200",
  approved: "bg-emerald-100 text-emerald-700 ring-emerald-200",
};
const RESOURCE_LINK = {
  invoice: (id) => `/invoices/${id}`,
  delivery_order: (id) => `/delivery-orders/${id}`,
  quotation: () => `/quotations`,
  order: () => `/orders`,
  purchase_order: () => `/purchase-orders`,
};
const RESOURCE_LABEL = {
  invoice: "invoice",
  delivery_order: "delivery order",
  quotation: "quotation",
  order: "order",
  purchase_order: "purchase order",
};

function timeAgo(iso) {
  try { return formatDistanceToNow(parseISO(iso), { addSuffix: true }); }
  catch { return iso; }
}

function todayISO() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  // Return YYYY-MM-DD in local time so "today" matches the user's clock.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function ActivityFeed() {
  const { user } = useAuth();
  const [scope, setScope] = useState("all"); // "all" | "mine"

  const params = useMemo(() => {
    if (scope === "mine" && user?.id) {
      return { limit: 30, actor_id: user.id, since: todayISO() };
    }
    return { limit: 12 };
  }, [scope, user?.id]);

  const { data = [], isLoading } = useQuery({
    queryKey: ["dashboard-activity", scope, user?.id],
    queryFn: () => api.listAuditLogs(params),
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });

  const myStats = useMemo(() => {
    // Compute a quick per-action tally when we're in "mine" mode.
    if (scope !== "mine") return null;
    const totals = { created: 0, updated: 0, status_changed: 0, deleted: 0, converted: 0, other: 0 };
    for (const e of data) {
      if (totals[e.action] !== undefined) totals[e.action] += 1;
      else totals.other += 1;
    }
    return totals;
  }, [scope, data]);

  return (
    <div className="border border-border rounded-md bg-white" data-testid="dashboard-activity-feed">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Activity className="size-4 text-primary" />
          <h3 className="font-display font-semibold">Recent activity</h3>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full bg-slate-100 p-0.5 text-xs">
            <button
              data-testid="activity-scope-all"
              onClick={() => setScope("all")}
              className={`px-3 py-1 rounded-full transition ${
                scope === "all"
                  ? "bg-white shadow-sm font-semibold text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              All company
            </button>
            <button
              data-testid="activity-scope-mine"
              onClick={() => setScope("mine")}
              className={`px-3 py-1 rounded-full transition ${
                scope === "mine"
                  ? "bg-red-600 text-white font-semibold shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              disabled={!user?.id}
            >
              My activity today
            </button>
          </div>
          <span className="overline">
            {scope === "mine" ? `today · ${data.length}` : `last ${data.length || 0}`}
          </span>
        </div>
      </div>
      {scope === "mine" && myStats && (
        <div
          data-testid="activity-my-stats"
          className="grid grid-cols-2 sm:grid-cols-5 gap-2 p-3 bg-red-50/60 border-b border-border"
        >
          {[
            { k: "created", label: "Created", tone: "text-emerald-700" },
            { k: "updated", label: "Updated", tone: "text-sky-700" },
            { k: "status_changed", label: "Status changes", tone: "text-amber-800" },
            { k: "converted", label: "Conversions", tone: "text-indigo-700" },
            { k: "deleted", label: "Deleted", tone: "text-red-700" },
          ].map((s) => (
            <div key={s.k} className="text-center">
              <p className={`mono text-lg font-bold ${s.tone}`}>{myStats[s.k]}</p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      )}
      {isLoading ? (
        <p className="text-sm text-muted-foreground p-4">Loading…</p>
      ) : data.length === 0 ? (
        <p className="text-sm text-muted-foreground p-4 italic">
          {scope === "mine"
            ? "You haven't logged any activity today yet. Actions on invoices, quotations, POs and DOs will show here."
            : "No activity yet. Actions on invoices, quotations, POs and delivery orders will show here."}
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {data.map((e) => {
            const Icon = ACT_ICON[e.action] || Activity;
            const tone = ACT_TONE[e.action] || "bg-slate-100 text-slate-700 ring-slate-200";
            const link = RESOURCE_LINK[e.resource_type]?.(e.resource_id);
            const rLabel = RESOURCE_LABEL[e.resource_type] || e.resource_type;
            const meta = e.meta || {};
            let extra = "";
            if (e.action === "status_changed" && meta.to) extra = `→ ${meta.to}`;
            else if (e.action === "converted" && meta.to_order) extra = `→ ${meta.to_order}`;
            else if (e.action === "created" && meta.total !== undefined)
              extra = `${Number(meta.total).toLocaleString()} ${meta.currency || ""}`;
            return (
              <li key={e.id} className="flex items-start gap-3 px-4 py-3" data-testid={`activity-row-${e.id}`}>
                <span className={`size-8 grid place-items-center rounded-full ring-1 ring-inset shrink-0 ${tone}`}>
                  <Icon className="size-3.5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm leading-tight">
                    <span className="font-semibold">{e.actor_name || "system"}</span>{" "}
                    <span className="text-muted-foreground">
                      {e.action.replace("_", " ")} {rLabel}{" "}
                    </span>
                    {link ? (
                      <Link
                        to={link}
                        className="mono font-medium text-primary hover:underline"
                      >
                        {e.resource_label}
                      </Link>
                    ) : (
                      <span className="mono font-medium text-primary">{e.resource_label}</span>
                    )}
                    {extra && (
                      <span className="mono text-[11px] px-1.5 py-0.5 rounded bg-slate-50 border border-slate-200 text-slate-700 ml-1">
                        {extra}
                      </span>
                    )}
                  </p>
                  <p className="text-[10px] text-muted-foreground mono mt-0.5">
                    {e.actor_role && (
                      <span className="uppercase tracking-wider mr-2">
                        {e.actor_role}
                      </span>
                    )}
                    {timeAgo(e.ts)}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


function TrafficChart({ data, title, testid }) {
  if (!data || data.length === 0) return null;
  const KEYS = [
    { k: "quotations", label: "Quot.", color: "#F59E0B" },
    { k: "orders", label: "Orders", color: "#C8102E" },
    { k: "delivery_orders", label: "DO", color: "#0A0A0A" },
    { k: "invoices", label: "Inv.", color: "#16A34A" },
  ];
  const max = Math.max(
    1,
    ...data.flatMap((b) => KEYS.map((kk) => Number(b[kk.k] || 0)))
  );
  return (
    <div className="border border-border rounded-md bg-white p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className="size-4 text-primary" />
          <h3 className="font-display font-semibold">{title}</h3>
        </div>
        <div className="flex items-center gap-3 text-[10px] mono">
          {KEYS.map((kk) => (
            <span key={kk.k} className="flex items-center gap-1 text-muted-foreground">
              <span className="size-2.5 rounded-sm" style={{ background: kk.color }} />
              {kk.label}
            </span>
          ))}
        </div>
      </div>
      <div className="flex items-end gap-2 sm:gap-3 h-44 overflow-x-auto">
        {data.map((b, i) => (
          <div key={i} className="flex flex-col items-center flex-1 min-w-[60px]" data-testid={`${testid}-bucket-${i}`}>
            <div className="flex items-end gap-0.5 h-32 w-full justify-center">
              {KEYS.map((kk) => {
                const v = Number(b[kk.k] || 0);
                const h = max ? Math.max(2, Math.round((v / max) * 128)) : 2;
                return (
                  <div key={kk.k} className="flex flex-col items-center justify-end h-full">
                    <span className="text-[9px] mono text-muted-foreground mb-0.5">{v || ""}</span>
                    <div
                      className="w-2.5 sm:w-3 rounded-sm"
                      style={{ height: `${h}px`, background: kk.color }}
                      title={`${kk.label}: ${v}`}
                    />
                  </div>
                );
              })}
            </div>
            <p className="overline mt-1 text-[10px]">{b.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function OverdueTile({ overdue }) {
  const navigate = useNavigate();
  const totals = Object.entries(overdue?.totals_by_currency || {});
  const has = totals.length > 0;
  const worst = overdue?.worst_client;
  return (
    <button
      type="button"
      data-testid="stat-overdue"
      onClick={() => navigate(has ? "/reports?tab=outstanding&view=client" : "/reports?tab=outstanding")}
      className={`text-left rounded-md p-5 flex flex-col gap-3 border transition-colors ${
        has ? "bg-red-600 border-red-700 text-white hover:bg-red-700" : "bg-white border-border card-hover"
      }`}
    >
      <div className="flex items-start justify-between">
        <p className={`overline ${has ? "!text-red-100" : ""}`}>Overdue balance</p>
        <div className={`size-8 grid place-items-center rounded-md ${has ? "bg-white/15 text-white" : "bg-emerald-50 text-emerald-700"}`}>
          <AlarmClock className="size-4" strokeWidth={2.25} />
        </div>
      </div>
      <div className="font-display text-2xl font-bold tracking-tight leading-none mono">
        {has ? totals.map(([c, v]) => `${fmtMoney(v)} ${c}`).join(" · ") : "0.00"}
      </div>
      <p className={`text-xs ${has ? "text-red-100" : "text-muted-foreground"}`}>
        {has
          ? <>{overdue.invoice_count} invoice{overdue.invoice_count === 1 ? "" : "s"} · {overdue.client_count} client{overdue.client_count === 1 ? "" : "s"}
              {worst && <> — worst: <b>{worst.name}</b> {fmtMoney(worst.balance)}</>}
              <span className="inline-flex items-center gap-1 ml-1.5 underline">Chase now <ArrowRight className="size-3" /></span></>
          : "Nothing overdue — nice."}
      </p>
    </button>
  );
}

function Stat({ label, value, icon: Icon, accent, testid, sublabel }) {
  return (
    <div
      data-testid={testid}
      className="card-hover bg-white border border-border rounded-md p-5 flex flex-col gap-3"
    >
      <div className="flex items-start justify-between">
        <p className="overline">{label}</p>
        <div className={`size-8 grid place-items-center rounded-md ${accent}`}>
          <Icon className="size-4" strokeWidth={2.25} />
        </div>
      </div>
      <div className="font-display text-3xl font-bold tracking-tight leading-none">
        {value}
      </div>
      {sublabel && <p className="text-xs text-muted-foreground">{sublabel}</p>}
    </div>
  );
}


function SoftwareExpiryCard() {
  const { data } = useQuery({
    queryKey: ["software-expiring", 30],
    queryFn: () => api.listExpiringSoftware(30),
    refetchInterval: 10 * 60 * 1000,
  });
  const [renewalItem, setRenewalItem] = useState(null);
  const items = data?.items || [];
  if (items.length === 0) return null;
  const critical = items.filter((it) => it.days_left <= 7).length;
  const top = items.slice(0, 3);
  return (
    <div
      data-testid="software-expiry-card"
      data-count={items.length}
      data-critical={critical}
      className={`rounded-md p-3 mb-4 ring-1 transition-all ${
        critical > 0 ? "ring-red-200 bg-red-50" : "ring-amber-200 bg-amber-50"
      }`}
    >
      <Link to="/inventory" className="flex items-center gap-3 hover:brightness-95">
        <div className={`p-2 rounded-md bg-white/60 ${critical > 0 ? "text-red-700" : "text-amber-800"}`}>
          <KeyRound className="size-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-semibold ${critical > 0 ? "text-red-800" : "text-amber-800"}`}>
            {items.length} software license{items.length !== 1 ? "s" : ""} expiring in the next 30 days
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {top.map((it) => `${it.name} (${it.days_left}d)`).join(" · ")}
            {items.length > 3 ? ` · +${items.length - 3} more` : ""}
          </div>
        </div>
        <div className={`flex items-center gap-1.5 text-xs font-semibold ${critical > 0 ? "text-red-700" : "text-amber-700"}`}>
          Inventory <ArrowRight className="size-3.5" />
        </div>
      </Link>
      <div className="flex flex-wrap gap-1.5 mt-2.5 pl-11" data-testid="renew-quick-row">
        {top.map((it) => (
          <button
            key={it.id}
            type="button"
            data-testid={`renew-quick-${it.id}`}
            onClick={(e) => { e.preventDefault(); setRenewalItem(it); }}
            className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-md bg-white ring-1 ring-inset ring-emerald-200 text-emerald-700 hover:ring-emerald-400 hover:bg-emerald-50"
            title="Create renewal quotation"
          >
            <FileSignature className="size-3" /> Renew {it.name}
          </button>
        ))}
      </div>
      <RenewalQuotationDialog
        open={!!renewalItem}
        onOpenChange={(v) => !v && setRenewalItem(null)}
        item={renewalItem}
      />
    </div>
  );
}


function BackupHealthCard() {
  const { data } = useQuery({
    queryKey: ["backup-status"],
    queryFn: api.getBackupStatus,
    refetchInterval: 5 * 60 * 1000, // 5 minutes
    staleTime: 60 * 1000,
  });
  if (!data) return null;

  const { status, last_backup_at, hours_since, count } = data;
  const style =
    status === "healthy"
      ? { ring: "ring-emerald-200", bg: "bg-emerald-50", text: "text-emerald-800",
          Icon: ShieldCheck, label: "Backups healthy" }
      : status === "stale"
      ? { ring: "ring-amber-200", bg: "bg-amber-50", text: "text-amber-800",
          Icon: AlarmClock, label: "Backup is stale" }
      : { ring: "ring-red-200", bg: "bg-red-50", text: "text-red-800",
          Icon: ShieldAlert, label: "No recent backup" };
  const Icon = style.Icon;

  let rel = "";
  if (last_backup_at) {
    try { rel = formatDistanceToNow(parseISO(last_backup_at), { addSuffix: true }); }
    catch { rel = last_backup_at; }
  }

  return (
    <Link
      to="/settings"
      data-testid="backup-health-card"
      data-status={status}
      className={`flex items-center gap-3 rounded-md p-3 mb-4 ring-1 ${style.ring} ${style.bg} hover:brightness-95 transition-all`}
    >
      <div className={`p-2 rounded-md bg-white/60 ${style.text}`}>
        <Icon className="size-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-semibold ${style.text}`}>{style.label}</div>
        <div className="text-xs text-muted-foreground truncate">
          {last_backup_at ? (
            <>Last backup {rel}
              {hours_since !== null && hours_since !== undefined && (
                <> · <span className="mono">{hours_since}h ago</span></>
              )}
              {" · "}<span className="mono">{count} on disk</span>
            </>
          ) : (
            <>No scheduled backup on disk yet — set up the daily cron.</>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1.5 text-xs font-semibold text-primary">
        <Database className="size-3.5" /> Settings <ArrowRight className="size-3.5" />
      </div>
    </Link>
  );
}


function OpenRfqsCard() {
  const { data, isLoading } = useQuery({
    queryKey: ["rfq-dashboard-open"],
    queryFn: () => api.rfqDashboardOpen(),
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  if (isLoading) return null;
  const items = data?.items || [];
  const totalOpen = data?.total_open || 0;
  const overdueCount = data?.overdue_count || 0;
  if (totalOpen === 0) return null;

  const statusTone = {
    draft: "bg-slate-100 text-slate-700 ring-slate-200",
    sent: "bg-sky-50 text-sky-700 ring-sky-200",
    responded: "bg-amber-50 text-amber-800 ring-amber-200",
  };

  return (
    <div
      data-testid="dashboard-open-rfqs-card"
      data-total-open={totalOpen}
      data-overdue={overdueCount}
      className="border border-border rounded-md bg-white"
    >
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardList className="size-4 text-primary" />
          <h3 className="font-display font-semibold">Open RFQs & deadlines</h3>
          {overdueCount > 0 && (
            <span
              data-testid="open-rfqs-overdue-pill"
              className="text-[10px] font-semibold px-1.5 py-0.5 rounded ring-1 ring-inset bg-red-50 text-red-700 ring-red-200"
            >
              {overdueCount} overdue
            </span>
          )}
        </div>
        <Link to="/rfqs">
          <Button
            data-testid="dashboard-view-all-rfqs"
            variant="ghost"
            size="sm"
            className="text-primary"
          >
            View all ({totalOpen}) <ArrowRight className="size-3.5 ml-1" />
          </Button>
        </Link>
      </div>
      <div className="p-2">
        <ul className="divide-y divide-border" data-testid="open-rfqs-list">
          {items.map((r) => {
            const dl = r.days_left;
            let badge = null;
            if (dl === null || dl === undefined) {
              badge = (
                <span
                  data-testid={`rfq-deadline-badge-${r.id}`}
                  className="text-[10px] mono px-1.5 py-0.5 rounded ring-1 ring-inset bg-slate-50 text-slate-600 ring-slate-200"
                >
                  no deadline
                </span>
              );
            } else if (r.is_overdue) {
              badge = (
                <span
                  data-testid={`rfq-deadline-badge-${r.id}`}
                  className="text-[10px] mono font-semibold px-1.5 py-0.5 rounded ring-1 ring-inset bg-red-50 text-red-700 ring-red-200"
                >
                  {Math.abs(dl)}d overdue
                </span>
              );
            } else if (dl <= 3) {
              badge = (
                <span
                  data-testid={`rfq-deadline-badge-${r.id}`}
                  className="text-[10px] mono font-semibold px-1.5 py-0.5 rounded ring-1 ring-inset bg-amber-50 text-amber-800 ring-amber-200"
                >
                  in {dl}d
                </span>
              );
            } else {
              badge = (
                <span
                  data-testid={`rfq-deadline-badge-${r.id}`}
                  className="text-[10px] mono px-1.5 py-0.5 rounded ring-1 ring-inset bg-emerald-50 text-emerald-700 ring-emerald-200"
                >
                  in {dl}d
                </span>
              );
            }
            return (
              <li
                key={r.id}
                data-testid={`open-rfq-row-${r.id}`}
                className="flex items-center justify-between gap-3 px-3 py-2.5"
              >
                <div className="min-w-0">
                  <Link
                    to={`/rfqs?open=${r.id}`}
                    className="font-medium text-sm mono hover:underline"
                  >
                    {r.rfq_number}
                  </Link>
                  <p className="text-xs text-muted-foreground truncate">
                    {r.supplier_name || "—"}
                    {" · "}
                    <span className="mono">{r.quotes_count} bid{r.quotes_count === 1 ? "" : "s"}</span>
                    {r.valid_until && (
                      <>
                        {" · due "}
                        <span className="mono">{fmtDate(r.valid_until)}</span>
                      </>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span
                    className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ring-1 ring-inset ${statusTone[r.status] || statusTone.draft}`}
                  >
                    {r.status}
                  </span>
                  {badge}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}


function LicenceRenewalsCard() {
  const { data, isLoading } = useQuery({
    queryKey: ["licence-summary"],
    queryFn: () => api.licenceDashboardSummary(),
    refetchInterval: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  if (isLoading) return null;
  const counts = data?.counts || { expired: 0, expiring_soon: 0, total: 0 };
  const items = data?.items || [];
  // Hide the whole card until at least one licence exists so brand-new
  // installs don't see an empty box on the dashboard.
  if (!counts.total) return null;

  return (
    <div
      data-testid="dashboard-licence-renewals-card"
      data-expired={counts.expired}
      data-expiring={counts.expiring_soon}
      className="border border-border rounded-md bg-white"
    >
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <KeyRound className="size-4 text-primary" />
          <h3 className="font-display font-semibold">Licence renewals</h3>
          {counts.red_hot > 0 && (
            <span
              data-testid="licence-heat-pill"
              className="text-[10px] font-semibold px-1.5 py-0.5 rounded ring-1 ring-inset bg-red-600 text-white ring-red-700 inline-flex items-center gap-1"
            >
              <Flame className="size-3" /> {counts.red_hot} red-hot
            </span>
          )}
          {counts.expired > 0 && (
            <span
              data-testid="licence-expired-pill"
              className="text-[10px] font-semibold px-1.5 py-0.5 rounded ring-1 ring-inset bg-red-50 text-red-700 ring-red-200"
            >
              {counts.expired} expired
            </span>
          )}
          {counts.expiring_soon > 0 && (
            <span
              data-testid="licence-expiring-pill"
              className="text-[10px] font-semibold px-1.5 py-0.5 rounded ring-1 ring-inset bg-amber-50 text-amber-800 ring-amber-200"
            >
              {counts.expiring_soon} due ≤30d
            </span>
          )}
        </div>
        <Link to="/licences">
          <Button
            data-testid="dashboard-view-all-licences"
            variant="ghost"
            size="sm"
            className="text-primary"
          >
            Open Licences <ArrowRight className="size-3.5 ml-1" />
          </Button>
        </Link>
      </div>
      {items.length === 0 ? (
        <div className="p-4 text-xs text-muted-foreground">
          {counts.active} active licence{counts.active === 1 ? "" : "s"} · nothing due within 30 days.
        </div>
      ) : (
        <ul className="divide-y divide-border" data-testid="licence-watchlist">
          {items.map((r) => {
            const days = r.days_until_expiry;
            const overdue = r.expiry_state === "expired";
            const hot = !!r.heat;
            const badgeCls = hot
              ? "bg-red-600 text-white ring-red-700"
              : overdue
              ? "bg-red-50 text-red-700 ring-red-200"
              : "bg-amber-50 text-amber-800 ring-amber-200";
            return (
              <li
                key={r.id}
                data-testid={`licence-watchlist-row-${r.id}`}
                data-heat={hot ? "true" : "false"}
                className={`px-4 py-2.5 flex items-center justify-between gap-3 ${hot ? "bg-red-50/70 border-l-2 border-l-red-600" : ""}`}
              >
                <div className="min-w-0">
                  <p className={`text-sm font-medium truncate flex items-center gap-1.5 ${hot ? "text-red-700" : ""}`}>
                    {hot && <Flame className="size-3.5 shrink-0 text-red-600" />}
                    {r.product_name}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {r.vendor || "—"}
                    {r.client_snapshot?.name ? ` · ${r.client_snapshot.name}` : ""}
                    {" · "}
                    <span className="mono">{r.expiry_date || "—"}</span>
                    {hot && (
                      <span className="text-red-600 font-semibold">
                        {" · "}{r.seat_usage_pct}% seats used
                      </span>
                    )}
                  </p>
                </div>
                <span
                  className={`shrink-0 text-[10px] mono font-semibold px-1.5 py-0.5 rounded ring-1 ring-inset ${badgeCls}`}
                >
                  {overdue ? `${Math.abs(days)}d overdue` : `in ${days}d`}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}



export default function Dashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ["dashboard"], queryFn: api.dashboard });
  const { data: traffic } = useQuery({ queryKey: ["traffic"], queryFn: api.trafficStats });
  const canSeeBackup = user && (user.role === "admin" || user.role === "finance");

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        overline={t("dashboard")}
        title={t("hero_title")}
        subtitle={t("hero_sub")}
        testid="dashboard-header"
      />

      {isLoading ? (
        <p className="text-muted-foreground">{t("loading")}</p>
      ) : (
        <>
          {canSeeBackup && <BackupHealthCard />}
          <SoftwareExpiryCard />
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
            <OverdueTile overdue={data.overdue} />
            <LicenceBudgetAlert variant="tile" />
            <BillsDueTile />
            <Stat label={t("total_clients")} value={data.clients_count}
              icon={Users} accent="bg-amber-50 text-amber-800" testid="stat-clients" />
            <Stat label={t("total_items")} value={data.items_count}
              icon={Package} accent="bg-emerald-50 text-emerald-700" testid="stat-items" />
            <Stat label={t("total_orders")} value={data.orders_count}
              icon={FileText} accent="bg-amber-50 text-amber-800" testid="stat-orders" />
            <Stat
              label={t("active_assets")}
              value={data.active_assets_count}
              sublabel={`/ ${data.client_assets_count} total`}
              icon={Boxes}
              accent="bg-slate-100 text-slate-800"
              testid="stat-active-assets"
            />
            <Stat
              label={t("inventory_value")}
              value={fmtMoney(data.total_inventory_value)}
              icon={Wallet}
              accent="bg-amber-50 text-amber-700"
              testid="stat-inventory-value"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <Stat
              label={t("do_pending")}
              value={data.delivery_orders.pending}
              icon={Truck}
              accent="bg-amber-50 text-amber-700"
              testid="stat-do-pending"
            />
            <Stat
              label={t("do_in_transit")}
              value={data.delivery_orders.in_transit}
              icon={Truck}
              accent="bg-slate-100 text-slate-800"
              testid="stat-do-intransit"
            />
            <Stat
              label={t("do_delivered")}
              value={data.delivery_orders.delivered}
              icon={Truck}
              accent="bg-emerald-50 text-emerald-700"
              testid="stat-do-delivered"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            {traffic && <TrafficChart data={traffic.quarters} title="Traffic — last 7 quarters" testid="traffic-quarters" />}
            {traffic && <TrafficChart data={traffic.years} title="Traffic — yearly" testid="traffic-years" />}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Expiring warranty / support */}
            <div className="border border-border rounded-md bg-white lg:col-span-2" data-testid="expiring-assets-card">
              <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="size-4 text-amber-600" />
                  <h3 className="font-display font-semibold">{t("expiring_soon")}</h3>
                </div>
                <span className="overline">{(data.expiring_assets || []).length}</span>
              </div>
              <div className="p-2">
                {(data.expiring_assets || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground p-4">{t("no_data")}</p>
                ) : (
                  <ul className="divide-y divide-border" data-testid="expiring-assets-list">
                    {data.expiring_assets.map((a) => (
                      <li key={a.id} className="flex items-center justify-between px-3 py-2.5">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="size-8 grid place-items-center rounded-md bg-amber-50 text-amber-700 shrink-0">
                            <ShieldCheck className="size-4" />
                          </span>
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{a.name}{a.serial_number ? ` · ${a.serial_number}` : ""}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              <Link to={`/clients/${a.client_id}`} className="hover:underline">
                                {a.client_snapshot?.name || "—"}
                              </Link>
                              {a.brand ? ` · ${a.brand}` : ""}
                              {a.model ? ` ${a.model}` : ""}
                            </p>
                          </div>
                        </div>
                        <div className="text-right text-xs space-y-0.5">
                          {a.warranty_end && (
                            <p className="flex items-center justify-end gap-1 text-amber-700">
                              <ShieldCheck className="size-3" /> {t("warranty")}: <span className="mono">{fmtDate(a.warranty_end)}</span>
                            </p>
                          )}
                          {a.support_end && (
                            <p className="flex items-center justify-end gap-1 text-slate-800">
                              <LifeBuoy className="size-3" /> {t("support")}: <span className="mono">{fmtDate(a.support_end)}</span>
                            </p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Low stock */}
            <div className="border border-border rounded-md bg-white">
              <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="size-4 text-amber-600" />
                  <h3 className="font-display font-semibold">{t("low_stock")}</h3>
                </div>
                <span className="overline">{data.low_stock_items.length}</span>
              </div>
              <div className="p-2">
                {data.low_stock_items.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-4">{t("no_data")}</p>
                ) : (
                  <ul className="divide-y divide-border" data-testid="low-stock-list">
                    {data.low_stock_items.map((it) => (
                      <li key={it.id} className="flex items-center justify-between px-3 py-2.5">
                        <div>
                          <p className="font-medium text-sm">{it.name}</p>
                          <p className="text-xs text-muted-foreground">{it.specification || "—"}</p>
                        </div>
                        <div className="text-right mono">
                          <p className="text-sm font-semibold text-red-600">{it.stock_qty} {it.unit}</p>
                          <p className="text-[10px] text-muted-foreground">/ {it.reorder_level}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Recent DOs */}
            <div className="border border-border rounded-md bg-white">
              <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Truck className="size-4 text-primary" />
                  <h3 className="font-display font-semibold">{t("recent_dos")}</h3>
                </div>
                <Link to="/delivery-orders">
                  <Button data-testid="dashboard-view-all-dos" variant="ghost" size="sm" className="text-primary">
                    {t("view")} <ArrowRight className="size-3.5 ml-1" />
                  </Button>
                </Link>
              </div>
              <div className="p-2">
                {data.recent_delivery_orders.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-4">{t("no_data")}</p>
                ) : (
                  <ul className="divide-y divide-border" data-testid="recent-dos-list">
                    {data.recent_delivery_orders.map((d) => (
                      <li key={d.id} className="flex items-center justify-between px-3 py-2.5">
                        <div>
                          <Link to={`/delivery-orders/${d.id}`} className="font-medium text-sm mono hover:underline">
                            {d.do_number}
                          </Link>
                          <p className="text-xs text-muted-foreground">
                            {d.client_snapshot?.name} · {fmtDate(d.delivery_date)}
                          </p>
                        </div>
                        <StatusBadge value={d.status} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* Open RFQs & deadlines */}
          <div className="mt-6">
            <OpenRfqsCard />
          </div>

          {/* Software licence renewals */}
          <div className="mt-6">
            <LicenceRenewalsCard />
          </div>

          {/* Company-wide activity feed */}
          <div className="mt-6">
            <ActivityFeed />
          </div>
        </>
      )}
    </div>
  );
}
